import { Leaf, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from './Button'

interface EmptyStateProps {
  icon?: ReactNode
  title: string
  body?: ReactNode
  action?: ReactNode
}

export function EmptyState({ icon, title, body, action }: EmptyStateProps) {
  return (
    <section className="grid justify-items-center gap-3 rounded-card border border-line/60 bg-surface-tint px-6 py-12 text-center">
      <span className="grid size-12 place-items-center rounded-full bg-surface text-brand shadow-card ring-4 ring-canvas/80">
        {icon ?? <Leaf className="size-5" />}
      </span>
      <h2 className="font-display text-title-sm text-ink">{title}</h2>
      {body && <p className="max-w-sm text-sm text-ink-soft">{body}</p>}
      {action}
    </section>
  )
}

interface ErrorStateProps {
  title?: string
  body?: ReactNode
  onRetry?: () => void
  retryLabel?: string
}

export function ErrorState({
  title = 'Something needs another try',
  body = 'We could not load this just now. Your data is safe.',
  onRetry,
  retryLabel = 'Try again',
}: ErrorStateProps) {
  return (
    <section
      role="alert"
      className="grid justify-items-center gap-3 rounded-card border border-line/60 bg-surface-tint px-6 py-12 text-center"
    >
      <span className="grid size-12 place-items-center rounded-full bg-surface text-warning shadow-card ring-4 ring-canvas/80">
        <RefreshCw className="size-5" />
      </span>
      <h2 className="font-display text-title-sm text-ink">{title}</h2>
      <p className="max-w-sm text-sm text-ink-soft">{body}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </section>
  )
}

export function ErrorBanner({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <p
      role="alert"
      className="rounded-control border border-danger/25 bg-danger-tint px-4 py-3 text-sm font-semibold text-danger"
    >
      {children}
    </p>
  )
}
